import { useRef } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { customFieldsApi } from './customFields.api';
import { customFieldsKeys } from './customFields.keys';
import type {
  CustomFieldsListResponse,
  CustomFormField,
  CustomFormFieldsParams,
  CustomFormFieldsResponse,
} from './customFields.interface';

type QueryOptions<TResponse, TData> = Omit<
  UseQueryOptions<TResponse, Error, TData, QueryKey>,
  'queryKey' | 'queryFn'
>;

// Settings list for one subject (custom fields + form template).
export const useCustomFieldsList = <TData = CustomFieldsListResponse>(
  subjectType: string = 'requisition',
  options?: QueryOptions<CustomFieldsListResponse, TData>
) =>
  useQuery({
    queryKey: customFieldsKeys.list(subjectType),
    queryFn: () => customFieldsApi.getFields(subjectType),
    ...options,
  });

// Fields a record form renders for a subject and department/office scope.
export const useCustomFormFields = <TField = CustomFormField>(
  params: CustomFormFieldsParams,
  options?: QueryOptions<
    CustomFormFieldsResponse<TField>,
    CustomFormFieldsResponse<TField>
  >
) =>
  useQuery({
    queryKey: customFieldsKeys.formFields(params),
    queryFn: () => customFieldsApi.getFormFields<TField>(params),
    ...options,
  });

// Form fields for a scope that changes while editing; keeps the last fields through refetches and failures.
export const useScopedCustomFormFields = <TField = CustomFormField>(
  params: CustomFormFieldsParams
) => {
  const query = useCustomFormFields<TField>(params, {
    placeholderData: keepPreviousData,
  });
  const lastData = useRef(query.data);
  if (query.data) lastData.current = query.data;
  return { ...query, data: query.data ?? lastData.current };
};

// Drops cached form fields so forms load the latest settings.
export const clearCustomFormFieldsCache = (queryClient: QueryClient) =>
  queryClient.removeQueries({ queryKey: customFieldsKeys.formFieldsAll() });

export const useCreateCustomField = () =>
  useMutation({ mutationFn: customFieldsApi.createCustomField });

export const useUpdateCustomField = () =>
  useMutation({ mutationFn: customFieldsApi.updateCustomField });

export const useDeleteCustomField = () =>
  useMutation({ mutationFn: customFieldsApi.deleteCustomField });

export const useUpdateFormFields = () =>
  useMutation({ mutationFn: customFieldsApi.updateFormFields });

export const useValidateOfferFormFields = () =>
  useMutation({ mutationFn: customFieldsApi.validateOfferFormFields });
